import React from 'react';
import PropTypes from 'prop-types';

import Collection from 'Components/UI/Meta/MetaControl/Types/Collection';
import ThrowInDev from 'Helpers/ThrowInDev';
import MultiPicklist from './Types/MultiPicklist';
import Picklist from './Types/Picklist';
import StringInput from './Types/String';
import DateInput from './Types/Date';
import Bool from './Types/Bool';
import NumberInput from './Types/Number';
import Form from './Types/Form';

import { getCustomProps, getSpreadProps } from './utils';
import { isFieldVisible } from '../isFieldVisible';

export const META_TYPE_CONTROL_MAP = {
  picklist: Picklist,
  multipicklist: MultiPicklist,
  string: StringInput,
  number: NumberInput,
  date: DateInput,
  email: StringInput,
  bool: Bool,
  array: Form,
  collection: Collection,
  phone: StringInput,
};

function MetaControl(props) {
  const {
    attrName,
    resource,
    metaOverrideSet,
    HelperProps,
    CustomProps,
    InputProps,
    ...rest
  } = props;

  if (!resource) {
    return <ThrowInDev err={`Resource is not defined but MetaControl tried to render it. Looking for attribute "${attrName}"`} />;
  }

  // If a field doesn't exist we need to abort early.
  // In prod a concern is that the field may have existed in the past but now the server got an update and no longer sends it, while the client could have an old bundle that still renders it
  if (resource.meta().field(attrName).isEmpty()) {
    return <ThrowInDev err={`Resource "${resource.type()}" does not have attribute "${attrName}" but MetaControl tried to render it`} />;
  }

  const metaField = resource.metaField(attrName, { metaOverrideSet }); // Globally overrides have occurred. Possibly local overrides have occurred.
  if (!metaField) {
    throw new Error(`Meta not specified for attribute: ${attrName}`);
  }

  const spreadProps = getSpreadProps(props, metaField.attr('_spreadPropNames'));
  const customProps = getCustomProps(props, CustomProps, metaField.attr('_customProps'));
  const customHelperProps = metaField.attr('_customHelperProps') || {};
  const combinedHelperProps = {
    ...HelperProps,
    ...customHelperProps,
  };

  const Component = META_TYPE_CONTROL_MAP[metaField.attr('type')];

  if (!Component) {
    throw new Error(`Unknown meta type "${metaField.attr('type')}" specified for attribute "${attrName}" of resource "${resource.type()}"`);
  }

  const {
    InputProps: customPropsInputProps,
    ...restCustomProps
  } = customProps;

  if (!isFieldVisible(resource, metaField)) {
    return null;
  }

  return (
    <Component
      test={`attr-${attrName}`}
      attrName={attrName}
      resource={resource}
      metaField={metaField}
      SpreadProps={spreadProps}
      CustomProps={restCustomProps}
      HelperProps={combinedHelperProps}
      InputProps={{
        ...InputProps,
        ...customPropsInputProps,
      }}
      placeholder={metaField.attr('placeholder')}
      {...rest}
    />
  );
}

MetaControl.defaultProps = {
  metaOverrideSet: null,
  HelperProps: {},
  InputProps: {},
};

MetaControl.propTypes = {
  attrName: PropTypes.string.isRequired,
  resource: PropTypes.object.isRequired,
  metaOverrideSet: PropTypes.object,
  HelperProps: PropTypes.object,
  InputProps: PropTypes.object,
};

export default MetaControl;
